/**
 * §7.16 Border Control. Live simulateMetapopulation across a multi-region
 * subset of the catalog, with a gravity-model OD matrix from regional
 * centroids + populations. Mobility-closure slider rescales the OD
 * matrix at runtime; the surface ranks regions by time-to-first-import
 * and by final attack rate.
 *
 * Engine plumbing exercised:
 *   - simulateMetapopulation (compartmental coupling across regions)
 *   - gravityODMatrix (Tizzoni-style β=2 default; configurable)
 *   - symmetriseODMatrix (commuter-style symmetric flow)
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateMetapopulation, symmetriseODMatrix } from '@biomax/plugin-epidemiology';
import { gravityODMatrix } from '@biomax/plugin-mobility';
import { Sparkline } from '../sparkline';

const DEFAULT_NETWORK = ['usa', 'can', 'mex', 'gbr', 'jpn', 'bra', 'aus'];

export function BorderControl() {
  const pathogens = listPathogens();
  const allRegions = listRegions();

  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [seedRegionId, setSeedRegionId] = useState<string>('usa');
  const [networkIds, setNetworkIds] = useState<string[]>(DEFAULT_NETWORK);
  const [mobilityClosure, setMobilityClosure] = useState<number>(0); // 0 = open, 1 = fully closed
  const [gravityBeta, setGravityBeta] = useState<number>(2);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    if (!pathogen) return null;
    const regions = networkIds.map(id => findRegion(id)).filter(Boolean) as ReturnType<typeof findRegion>[];
    const valid = regions.filter(r => r && r.geometry?.centroidLatLon) as NonNullable<ReturnType<typeof findRegion>>[];
    if (valid.length < 2) return null;
    const seedIdx = valid.findIndex(r => r.id === seedRegionId);
    if (seedIdx < 0) return null;

    const mobRegions = valid.map(r => ({
      id: r.id,
      population: r.population.total,
      centroidLatLon: r.geometry!.centroidLatLon as [number, number],
    }));
    // Gravity calibration: pick k so the largest pairwise flow is ~0.1% of
    // the smaller region per day (reasonable upper bound for aviation
    // commuter-equivalent on long-haul links).
    const rawOd = gravityODMatrix(mobRegions, { beta: gravityBeta, k: 1 });
    let maxFlow = 0;
    for (let i = 0; i < rawOd.length; i++) {
      for (let j = 0; j < rawOd.length; j++) {
        if (i === j) continue;
        const cap = Math.min(mobRegions[i]!.population, mobRegions[j]!.population);
        if (cap > 0 && rawOd[i]![j]! / cap > maxFlow) maxFlow = rawOd[i]![j]! / cap;
      }
    }
    const kScale = maxFlow > 0 ? 0.001 / maxFlow : 1;
    const scaledOd = symmetriseODMatrix(
      rawOd.map(row => row.map(v => v * kScale * (1 - mobilityClosure)))
    );

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;

    const M = valid.length;
    const sim = simulateMetapopulation({
      regions: valid.map((r, i) => ({
        N: r.population.total,
        I0: i === seedIdx ? 500 : 0,
      })),
      betaPerRegion: new Array(M).fill(beta),
      gammaPerRegion: new Array(M).fill(gamma),
      sigmaPerRegion: new Array(M).fill(sigma),
      odMatrix: scaledOd,
      durationDays: 120, dtDays: 1,
    });

    // Per-region: time-to-first-import = first day where I_i > IMPORT_THRESHOLD.
    const IMPORT_THRESHOLD = 5;
    const perRegion = valid.map((r, i) => {
      const series = sim.compartments[`I_${i}`]!;
      const peakI = Math.max(...series);
      const peakDay = series.indexOf(peakI);
      const final = sim.compartments[`R_${i}`]!.at(-1)!;
      const attackRate = final / r.population.total;
      let firstImport: number | null = null;
      if (i !== seedIdx) {
        for (let t = 0; t < series.length; t++) {
          if (series[t]! >= IMPORT_THRESHOLD) { firstImport = t; break; }
        }
      }
      return { region: r, series, peakI, peakDay, attackRate, firstImport };
    });

    return { sim, perRegion, seedIdx };
  }, [pathogenId, seedRegionId, networkIds, mobilityClosure, gravityBeta]);

  if (!view) return <div>Loading…</div>;

  const ranked = view.perRegion
    .map((r, i) => ({ ...r, originalIdx: i }))
    .filter(r => r.originalIdx !== view.seedIdx)
    .sort((a, b) => (a.firstImport ?? Infinity) - (b.firstImport ?? Infinity));

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.16</span>
        <h1 className="surface__title">Border Control · cross-region spread</h1>
      </div>
      <div className="surface__sub">
        Live metapopulation SEIR ({view.perRegion.length}-region) · gravity-model
        OD matrix · seed in {findRegion(seedRegionId)?.names.english} ·{' '}
        <span className="receipt"><span className="receipt__dot" />engine {view.sim.receipt.engineVersion} · {view.sim.receipt.elapsedMs.toFixed(0)} ms · {view.sim.receipt.steps} steps</span>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Network</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Seed region</span>
            <select value={seedRegionId} onChange={e => setSeedRegionId(e.target.value)}>
              {networkIds.map(id => {
                const r = findRegion(id);
                return r ? <option key={id} value={id}>{r.names.english}</option> : null;
              })}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Mobility closure · {(mobilityClosure * 100).toFixed(0)}%</span>
            <input type="range" min="0" max="0.99" step="0.01" value={mobilityClosure}
              onChange={e => setMobilityClosure(parseFloat(e.target.value))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Gravity β · {gravityBeta.toFixed(1)}</span>
            <input type="range" min="1" max="3" step="0.1" value={gravityBeta}
              onChange={e => setGravityBeta(parseFloat(e.target.value))} />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <span className="simctl__label">Network composition · click to toggle</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bm-space-2)', marginTop: 8 }}>
            {allRegions
              .filter(r => r.geometry?.centroidLatLon)
              .slice()
              .sort((a, b) => a.names.english.localeCompare(b.names.english))
              .map(r => {
                const active = networkIds.includes(r.id);
                return (
                  <button
                    key={r.id}
                    className={active ? 'btn btn--primary' : 'btn'}
                    onClick={() => setNetworkIds(prev =>
                      prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id]
                    )}
                  >
                    {r.names.iso3 ?? r.id}
                  </button>
                );
              })}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Time-to-first-import · ranked</span>
          <span className="panel__meta">first day {`I_i ≥ 5`}</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Region</th>
              <th className="num">First-import day</th>
              <th className="num">Peak I</th>
              <th className="num">Peak day</th>
              <th className="num">Final attack rate</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: 'rgba(239,68,68,0.08)' }}>
              <td>—</td>
              <td><strong>{view.perRegion[view.seedIdx]!.region.names.english}</strong> (seed)</td>
              <td className="num">day 0</td>
              <td className="num">{Math.round(view.perRegion[view.seedIdx]!.peakI).toLocaleString()}</td>
              <td className="num">day {view.perRegion[view.seedIdx]!.peakDay}</td>
              <td className="num">{(view.perRegion[view.seedIdx]!.attackRate * 100).toFixed(1)}%</td>
            </tr>
            {ranked.map((r, i) => (
              <tr key={r.region.id}>
                <td>{i + 1}</td>
                <td>{r.region.names.english}</td>
                <td className="num" style={{ color: r.firstImport === null ? 'var(--bm-accent)' : 'var(--bm-text)' }}>
                  {r.firstImport === null ? 'never' : `day ${r.firstImport}`}
                </td>
                <td className="num">{Math.round(r.peakI).toLocaleString()}</td>
                <td className="num">{r.peakDay > 0 ? `day ${r.peakDay}` : '—'}</td>
                <td className="num">{(r.attackRate * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Per-region infectious trajectories</span>
          <span className="panel__meta">stacked sparklines</span>
        </div>
        {view.perRegion.map((r, i) => (
          <Sparkline
            key={r.region.id}
            values={r.series}
            label={`${r.region.names.english}${i === view.seedIdx ? ' (seed)' : ''}`}
            stroke={i === view.seedIdx ? 'var(--bm-danger)' : 'var(--bm-signal)'}
          />
        ))}
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Method + limitations</span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          OD matrix built from the gravity model T_ij = k · N_i · N_j / d_ij^β with
          β = {gravityBeta.toFixed(1)} (default β=2 follows Tizzoni et al. 2014; pattern
          mirrors GLEAM, Balcan + Vespignani 2010 PNAS). Matrix is symmetrised for
          commuter-style flow then scaled so the maximum pair flow is 0.1% of the
          smaller region's population per day — a coarse aviation/commuter proxy.
          <strong> v0.3 follow-up: bind the matrix to real published flows
          (DTU OpenSky aviation, EuroCommute commuter)</strong> rather than to a
          calibrated gravity proxy. Closing borders (closure = 1) zeroes the
          off-diagonals; partial closures (e.g., 50%) attenuate proportionally.
        </div>
      </div>
    </div>
  );
}
